import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Lock, Eye, EyeOff, X, Loader2, Folder, File } from 'lucide-react'
import { useVault } from '../contexts/VaultContext'
import toast from 'react-hot-toast'

const PasswordModal = ({ item, onClose, onSuccess }) => {
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  
  const { downloadFile, openFolder, folderView } = useVault()

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // For files inside folders, try to use cached password first
    if (!item.isFolder && folderView && folderView.password) {
      setLoading(true)
      try {
        // Try with cached folder password first
        const decryptedData = await downloadFile(item.path, null, item.displayName)
        
        // Pass decrypted data to parent for viewing
        onSuccess({
          data: decryptedData,
          fileName: item.displayName,
          mimeType: getMimeType(item.displayName)
        })
        
        toast.success(`Decrypted: ${item.displayName}`)
        onClose()
        return
      } catch (error) {
        // If cached password fails, continue to ask for password
        console.log('Cached password failed, asking user for password')
      } finally {
        setLoading(false)
      }
    }
    
    if (!password) {
      toast.error('Please enter the password')
      return
    }

    setLoading(true)

    try {
      if (item.isFolder) {
        // Open folder
        const manifest = await openFolder(item.displayName, password)
        toast.success(`Opened folder: ${item.displayName}`)
        onClose()
      } else {
        // Download and decrypt file
        const decryptedData = await downloadFile(item.path, password, item.displayName)
        
        // Pass decrypted data to parent for viewing
        onSuccess({
          data: decryptedData,
          fileName: item.displayName,
          mimeType: getMimeType(item.displayName)
        })
        
        toast.success(`Decrypted: ${item.displayName}`)
        onClose()
      }
    } catch (error) {
      toast.error(error.message)
    } finally {
      setLoading(false)
    }
  }

  const getMimeType = (fileName) => {
    const extension = fileName.split('.').pop()?.toLowerCase()
    const mimeTypes = {
      // Images
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      png: 'image/png',
      gif: 'image/gif',
      webp: 'image/webp',
      svg: 'image/svg+xml',
      // Videos
      mp4: 'video/mp4',
      webm: 'video/webm',
      avi: 'video/avi',
      mov: 'video/quicktime',
      // Documents
      pdf: 'application/pdf',
      txt: 'text/plain',
      md: 'text/markdown',
      json: 'application/json',
      csv: 'text/csv',
      // Default
      default: 'application/octet-stream'
    }
    
    return mimeTypes[extension] || mimeTypes.default
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="modal-overlay"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ type: "spring", duration: 0.3 }}
          className="modal-content"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                {item?.isFolder ? (
                  <Folder className="w-5 h-5 text-blue-600" />
                ) : (
                  <File className="w-5 h-5 text-blue-600" />
                )}
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  Unlock {item?.isFolder ? 'Folder' : 'File'}
                </h2>
                <p className="text-sm text-gray-600">
                  {item?.displayName}
                </p>
              </div>
            </div>
            
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={loading}
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Decryption Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-field pl-10 pr-10"
                  placeholder="Enter the password used for encryption"
                  required
                  disabled={loading}
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  disabled={loading}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 btn-secondary"
                disabled={loading}
              >
                Cancel
              </button>
              
              <button
                type="submit"
                disabled={loading || !password}
                className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {item?.isFolder ? 'Opening...' : 'Decrypting...'}
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-2">
                    <Lock className="w-4 h-4" />
                    Unlock
                  </div>
                )}
              </button>
            </div>
          </form>

          {/* Security Notice */}
          <div className="mt-6 p-3 bg-blue-50 rounded-lg">
            <p className="text-xs text-blue-700 text-center">
              🔒 This {item?.isFolder ? 'folder' : 'file'} is encrypted with end-to-end encryption. 
              Only you have the key to decrypt it.
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

export default PasswordModal
